import { useScrollReveal } from '@/hooks/useScrollReveal';

const steps = [
  {
    step: '01',
    label: 'Harvest',
    description: 'Fresh produce and seafood at peak quality',
    icon: 'ri-seedling-line',
    color: 'bg-secondary-500',
    textColor: 'text-secondary-600',
    bgTint: 'bg-secondary-50',
    border: 'border-secondary-200',
  },
  {
    step: '02',
    label: 'Process & Standardise',
    description: 'Cleaned, graded, weighed, vacuum-packed to spec',
    icon: 'ri-barcode-line',
    color: 'bg-accent-500',
    textColor: 'text-accent-600',
    bgTint: 'bg-accent-50',
    border: 'border-accent-200',
  },
  {
    step: '03',
    label: 'Freeze & Store',
    description: 'Locked in at -18°C across verified warehouses',
    icon: 'ri-snowflake-line',
    color: 'bg-brand-500',
    textColor: 'text-primary-600',
    bgTint: 'bg-primary-50',
    border: 'border-primary-200',
  },
  {
    step: '04',
    label: 'Move & Trade',
    description: 'Reefer transport and B2B marketplace',
    icon: 'ri-truck-line',
    color: 'bg-accent-500',
    textColor: 'text-accent-600',
    bgTint: 'bg-accent-50',
    border: 'border-accent-200',
  },
  {
    step: '05',
    label: 'Sell B2B / B2C',
    description: 'Delivered to restaurants, hotels, and homes',
    icon: 'ri-store-2-line',
    color: 'bg-secondary-600',
    textColor: 'text-secondary-700',
    bgTint: 'bg-secondary-50',
    border: 'border-secondary-300',
  },
];

export default function BrandStorySection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div
        ref={ref}
        className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}
      >
        {/* Section header */}
        <div className="text-center mb-14 md:mb-18 max-w-2xl mx-auto">
          <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-4">
            The Problem We Solve
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 leading-tight mb-5 text-balance">
            Every harvest has a moment of perfection.
          </h2>
          <p className="text-foreground-500 leading-relaxed">
            India&apos;s harvests are abundant and seasonal, but appetite is constant.
            Freshhlock moves the absorption point from the farm-gate to a freezer,
            so <strong className="text-primary-700 font-semibold">quality sets the price, not panic.</strong>
          </p>
        </div>

        {/* Connected Flow Pipeline */}
        <div className="relative">
          {/* Desktop connecting line */}
          <div className="hidden lg:block absolute top-[52px] left-[12.5%] right-[12.5%] h-0.5">
            <div className="h-full bg-gradient-to-r from-secondary-300 via-primary-300 via-accent-300 to-secondary-400 rounded-full"></div>
          </div>

          {/* Steps grid */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-4">
            {steps.map((item) => (
              <div
                key={item.step}
                className={`relative group rounded-2xl border ${item.border} ${item.bgTint} p-5 md:p-6 transition-all duration-300 shadow-sm hover:shadow-lg hover:-translate-y-1 card-animate`}
              >
                {/* Arrow connector - desktop only, between steps */}
                {item.step !== '05' && (
                  <div className="hidden lg:flex absolute top-1/2 -translate-y-1/2 -right-6 z-10 w-8 h-8 items-center justify-center"
                  >
                    <i className="ri-arrow-right-line text-foreground-400 text-lg group-hover:text-foreground-500 transition-colors"
                    ></i>
                  </div>
                )}

                {/* Step number badge */}
                <div className="flex items-center gap-2 mb-4">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold text-paper-50 ${item.color}`}
                  >
                    {item.step}
                  </span>
                  <span className={`text-xs font-semibold ${item.textColor} uppercase tracking-wider`}
                  >
                    Step {item.step}
                  </span>
                </div>

                {/* Icon */}
                <div className={`${item.color} w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center text-paper-50 mb-4 animate-icon-float icon-hover-pop`}
                >
                  <i className={`${item.icon} text-xl md:text-2xl`}
                  ></i>
                </div>

                {/* Label */}
                <h3 className="font-heading font-bold text-primary-700 text-base md:text-lg mb-2">
                  {item.label}
                </h3>

                {/* Description */}
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}